import { useCallback } from 'react';

// Import zone-based selectors and operations instead of regular ones
import { useReduxSelectorsZoneBased } from './useReduxSelectorsZoneBased';
import { useLitGroupOperationsZoneBased } from '../entities/useLitGroupOperationsZoneBased';
import { useBookOperationsZoneBased } from '../entities/useBookOperationsZoneBased';
import { useCharacterOperationsZoneBased } from '../entities/useCharacterOperationsZoneBased';
// ❌ EXCLUDED: useVariationOperations - belongs to Variation Zone
// ❌ EXCLUDED: useSampleOperations - belongs to Variation Zone
// ✅ FIX: Exclude old hooks that use useReduxSelectors
// import { useSelectionState } from './useSelectionState';
// import { useRelationshipMappers } from '../utilities/useRelationshipMappers';
// import { useFormStateManager } from '../utilities/useFormStateManager';

/**
 * 🚀 ZONE-BASED Main orchestrator hook for Character Zone only
 * This provides pure Character Zone isolation by excluding Variation Zone operations
 * Used by useStableBookWorksheet to prevent unnecessary re-renders
 * @returns {Object} Character Zone Book Worksheet interface
 */
export const useBookWorksheetCoreZoneBased = () => {
  // 🚀 ZONE-BASED: Use zone-based selectors that only subscribe to Character Zone data
  const selectors = useReduxSelectorsZoneBased();

  // Character Zone operations only - using zone-based versions
  const litGroups = useLitGroupOperationsZoneBased();
  const books = useBookOperationsZoneBased();
  const characters = useCharacterOperationsZoneBased();

  // ❌ EXCLUDED: Variation Zone operations
  // const variations = useVariationOperations();
  // const samples = useSampleOperations();

  // ✅ FIX: Create dummy implementations for excluded hooks that use old selectors
  const selectionStubs = {
    // Selection IDs (will be provided by selectors)
    litGroupId: null,
    bookId: null,
    characterId: null,
    variationId: null,

    // Selection setters (dummy implementations)
    setLitGroupSelection: () => { },
    setBookSelection: () => { },
    setCharacterSelection: () => { },
    setVariationSelection: () => { },

    // Selection clearers (dummy implementations)
    clearLitGroupSelection: () => { },
    clearBookSelection: () => { },
    clearCharacterSelection: () => { },
    clearVariationSelection: () => { },
    clearAllSelections: () => { },

    // Persist selections
    persistSelections: true,
    setPersistSelections: () => { }
  };

  const relationshipStubs = {
    // Relationship mapping stubs
    getVariationProps: () => ({}),
    variationProps: {}
  };

  const formStubs = {
    // Form state stubs
    characterFormState: {
      isDirty: false,
      isSubmitting: false,
      errors: null
    }
  };

  // 🚀 ZONE-BASED: Provide stub implementations for excluded Variation Zone operations
  // This maintains backward compatibility while preventing actual subscriptions
  const variationStubs = {
    // Variation operations stubs
    fetchVariations: () => Promise.resolve([]),
    createVariation: () => Promise.resolve(null),
    updateVariation: () => Promise.resolve(null),
    deleteVariation: () => Promise.resolve(null),

    // Sample operations stubs
    fetchVariationSamples: () => Promise.resolve([]),
    fetchVariationSamplesWithUrls: () => Promise.resolve([]),
    updateActiveSample: () => Promise.resolve(null),
    fetchVariation: () => Promise.resolve(null),

    // Variation props stub
    getVariationProps: () => ({}),

    // Other variation-related stubs
    variationProps: {}
  };

  // Return unified interface with Character Zone data + Variation Zone stubs
  return {
    // Spread Character Zone specialized hook results
    ...selectors,
    ...selectionStubs,
    ...litGroups,
    ...books,
    ...characters,
    ...relationshipStubs,
    ...formStubs,

    // 🚀 ZONE-BASED: Include Variation Zone stubs for backward compatibility
    ...variationStubs
  };
};

export default useBookWorksheetCoreZoneBased;
